import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, Search, ChevronLeft, ChevronRight,
  GitFork, Pencil, Trash2, ToggleLeft, ToggleRight, PlayCircle,
  AlertTriangle, CheckCircle2,
} from 'lucide-react';

import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { AppRoutes }       from '@/constants/routes';
import { Spinner }         from '@/components/ui/Spinner';
import { ConfirmDialog }   from '@/components/ui/ConfirmDialog';
import { cn }              from '@/utils/cn';
import { apiClient }       from '@/api/client';

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface GlAllocationTemplateListDto {
  id: string;
  templateCode: string;
  description: string;
  sourceAccountCode: string;
  allocationMethod: string;
  isActive: boolean;
  lineCount: number;
  createdDate: string;
}

interface GlAllocationFilter {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  allocationMethod?: string;
}

const METHODS = ['Fixed Percentage', 'Fixed Amount', 'Variable', 'Statistical'];

// â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const GlAllocationTemplateListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canCreate  = hasPermission(Permissions.GL_ALLOCATION_CREATE);
  const canEdit    = hasPermission(Permissions.GL_ALLOCATION_EDIT);
  const canDelete  = hasPermission(Permissions.GL_ALLOCATION_DELETE);
  const canExecute = hasPermission(Permissions.GL_ALLOCATION_EXECUTE);

  const [items,   setItems]   = useState<GlAllocationTemplateListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [filter, setFilter] = useState<GlAllocationFilter>({ page: 1, pageSize });
  const [search, setSearch] = useState('');

  const [deleting, setDeleting] = useState<GlAllocationTemplateListDto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [executeMsg, setExecuteMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async (f: GlAllocationFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page',     String(f.page));
      params.set('pageSize', String(f.pageSize));
      if (f.search)             params.set('search',           f.search);
      if (f.allocationMethod)   params.set('allocationMethod', f.allocationMethod);
      if (f.isActive !== undefined) params.set('isActive', String(f.isActive));

      const res  = await apiClient.get(`/gl-allocation-templates?${params}`);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load GL allocation templates', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const handleSearch = () => {
    setFilter(f => ({ ...f, search: search || undefined, page: 1 }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilter(f => ({ ...f, page: newPage }));
  };

  const handleToggleActive = async (id: string) => {
    try {
      await apiClient.post(`/gl-allocation-templates/${id}/toggle-status`);
      load(filter);
    } catch (err) { console.error('Toggle active failed', err); }
  };

  const handleDelete = (item: GlAllocationTemplateListDto) => setDeleting(item);

  const executeDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/gl-allocation-templates/${deleting.id}`);
      setDeleting(null);
      load(filter);
    } catch (err) { console.error('Delete failed', err); }
    finally { setDeleteLoading(false); }
  };

  const handleExecute = async (id: string) => {
    const periodCode = window.prompt('Enter Financial Period Code to execute allocation (e.g. FY2026-01):');
    if (!periodCode?.trim()) return;
    setExecuteMsg(null);
    try {
      await apiClient.post(`/gl-allocation-templates/${id}/execute`, {
        financialPeriodCode: periodCode.trim().toUpperCase(),
        executionDate: new Date().toISOString().split('T')[0],
      });
      setExecuteMsg({ type: 'success', text: 'Allocation executed. Journal entry created successfully.' });
      load(filter);
    } catch (err: any) {
      setExecuteMsg({ type: 'error', text: err.response?.data?.message ?? 'Execute failed. Please check the period code and try again.' });
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <GitFork size={20} className="text-teal-600 dark:text-teal-400" />
              GL Allocation Templates
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} templates
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(filter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors" title="Refresh">
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.GL_ALLOCATION_TEMPLATE_NEW)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Template
              </button>
            )}
          </div>
        </div>

        {/* â”€â”€ Filters â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search code or description..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>
          <select
            value={filter.allocationMethod ?? ''}
            onChange={e => { setFilter(f => ({ ...f, allocationMethod: e.target.value || undefined, page: 1 })); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2
                       bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
          >
            <option value="">All Methods</option>
            {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className="flex items-center gap-1.5">
            {([{ label: 'Active', value: true }, { label: 'Inactive', value: false }] as const).map(opt => (
              <button key={String(opt.value)}
                onClick={() => { setFilter(f => ({ ...f, isActive: f.isActive === opt.value ? undefined : opt.value, page: 1 })); setPage(1); }}
                className={cn('px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                  filter.isActive === opt.value
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                )}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {executeMsg && (
        <div className={cn(
          'flex items-center gap-2 px-6 py-2.5 text-sm',
          executeMsg.type === 'success'
            ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 border-b border-teal-200 dark:border-teal-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-b border-red-200 dark:border-red-800',
        )}>
          {executeMsg.type === 'success'
            ? <CheckCircle2 size={15} className="shrink-0" />
            : <AlertTriangle size={15} className="shrink-0" />}
          {executeMsg.text}
        </div>
      )}

      {/* â”€â”€ Table â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <GitFork size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No GL allocation templates found</p>
            {canCreate && (
              <button onClick={() => navigate(AppRoutes.GL_ALLOCATION_TEMPLATE_NEW)}
                className="mt-3 text-teal-600 dark:text-teal-400 text-sm hover:underline">
                Create your first template
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Method</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Active</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Lines</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(item => (
                <tr key={item.id}
                  onClick={() => canEdit && navigate(AppRoutes.GL_ALLOCATION_TEMPLATE_EDIT.replace(':id', item.id))}
                  className={cn('hover:bg-teal-50 dark:hover:bg-slate-700/50 transition-colors group', canEdit && 'cursor-pointer')}>
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400">{item.templateCode}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300 max-w-[220px] truncate">{item.description}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{item.allocationMethod}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('text-xs font-medium', item.isActive ? 'text-green-600 dark:text-green-400' : 'text-slate-400')}>
                      {item.isActive ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300">{item.lineCount}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{fmtDate(item.createdDate)}</td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <button
                          onClick={() => navigate(AppRoutes.GL_ALLOCATION_TEMPLATE_EDIT.replace(':id', item.id))}
                          title="Edit"
                          className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors">
                          <Pencil size={15} />
                        </button>
                      )}
                      {canEdit && (
                        <button onClick={() => handleToggleActive(item.id)}
                          title={item.isActive ? 'Deactivate' : 'Activate'}
                          className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors">
                          {item.isActive ? <ToggleRight size={16} className="text-green-600 dark:text-green-400" /> : <ToggleLeft size={16} />}
                        </button>
                      )}
                      {canExecute && item.isActive && (
                        <button onClick={() => handleExecute(item.id)}
                          title="Execute allocation"
                          className="p-1.5 rounded hover:bg-teal-100 text-teal-600 dark:text-teal-400 transition-colors">
                          <PlayCircle size={16} />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(item)}
                          title="Delete"
                          className="p-1.5 rounded hover:bg-red-100 text-red-500 transition-colors">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* â”€â”€ Pagination â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">Page {page} of {totalPages} · {total.toLocaleString()} records</p>
          <div className="flex items-center gap-1">
            <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button key={n} onClick={() => handlePageChange(n)}
                  className={cn('w-8 h-8 text-sm rounded transition-colors',
                    n === page ? 'bg-teal-600 text-white font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'
                  )}>{n}</button>
              );
            })}
            <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        variant="delete"
        title="Delete GL Allocation Template"
        message={<span>Delete GL allocation template <strong>{deleting?.templateCode}</strong>?</span>}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
        loading={deleteLoading}
        onConfirm={executeDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
};

